import { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence, useReducedMotion } from 'motion/react'
import { SuggestionChips } from '@/components/SuggestionChips'
import { FreeTextInput } from '@/components/FreeTextInput'
import { ProgressIndicator } from '@/components/ProgressIndicator'
import { BackButton } from '@/components/BackButton'
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts'
import { getAnimationVariants, AnimationDirection } from '@/lib/animations'

export interface FocusTileProps {
  question: string
  suggestions: string[]
  answer?: string
  progress: number
  currentStep: number
  totalSteps: number
  onAnswer: (answer: string, type: 'suggestion' | 'freetext') => void
  onBack?: () => void
  onRecalculate?: () => void
  canGoBack?: boolean
  canGoForward?: boolean
  isRecalculating?: boolean
  onPause?: () => void
}

interface AnswerState {
  value: string
  type: 'suggestion' | 'freetext'
}

export function FocusTile({
  question,
  suggestions,
  answer,
  progress,
  currentStep,
  totalSteps,
  onAnswer,
  onBack,
  onRecalculate,
  canGoBack = false,
  canGoForward = false,
  isRecalculating = false,
  onPause,
}: FocusTileProps) {
  const prefersReducedMotion = useReducedMotion() ?? false
  const [direction, setDirection] = useState<AnimationDirection>('forward')
  const [showFreeText, setShowFreeText] = useState(false)
  const [freeTextValue, setFreeTextValue] = useState('')
  const [answerState, setAnswerState] = useState<AnswerState | null>(null)

  const { variants, transition } = getAnimationVariants(direction, prefersReducedMotion)

  const handleSuggestionSelect = useCallback(
    (suggestion: string) => {
      onAnswer(suggestion, 'suggestion')
      setAnswerState({ value: suggestion, type: 'suggestion' })
    },
    [onAnswer]
  )

  const handleFreeTextSubmit = useCallback(
    (text: string) => {
      onAnswer(text, 'freetext')
      setAnswerState({ value: text, type: 'freetext' })
      setShowFreeText(false)
      setFreeTextValue('')
    },
    [onAnswer]
  )

  const handleBack = useCallback(() => {
    setDirection('backward')
    onBack?.()
  }, [onBack])

  const handleRecalculate = useCallback(() => {
    setDirection('recalculate')
    onRecalculate?.()
  }, [onRecalculate])

  const handleArrowLeft = useCallback(() => {
    if (canGoBack) handleBack()
  }, [canGoBack, handleBack])

  const handleArrowRight = useCallback(() => {
    if (canGoForward) {
      setDirection('forward')
    }
  }, [canGoForward])

  const handleUndo = useCallback(() => {
    // Will be implemented with history stack
  }, [])

  const handleRedo = useCallback(() => {
    // Will be implemented with history stack
  }, [])

  const handleEnter = useCallback(() => {
    if (showFreeText && freeTextValue.trim()) {
      handleFreeTextSubmit(freeTextValue.trim())
    } else if (answerState) {
      // Already answered, could move to next
    }
  }, [showFreeText, freeTextValue, answerState, handleFreeTextSubmit])

  const handleEscape = useCallback(() => {
    if (showFreeText) {
      setShowFreeText(false)
      setFreeTextValue('')
    } else {
      onPause?.()
    }
  }, [showFreeText, onPause])

  useKeyboardShortcuts({
    onEnter: handleEnter,
    onEscape: handleEscape,
    onArrowLeft: handleArrowLeft,
    onArrowRight: handleArrowRight,
    onUndo: handleUndo,
    onRedo: handleRedo,
    onRecalculate: handleRecalculate,
    enabled: true,
  })

  useEffect(() => {
    if (answer) {
      setDirection('forward')
    }
  }, [answer])

  const isAnswered = !!answer || !!answerState

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={direction}
        variants={variants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={transition}
        className="focus-tile"
        style={{ willChange: 'transform, opacity' }}
      >
        <div className="space-y-6">
          {/* Question Card */}
          <motion.div
            className="tile-question-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 text-sm text-text-muted mb-2">
                  <span className="badge-info">Step {currentStep} of {totalSteps}</span>
                  {isRecalculating && (
                    <motion.span
                      className="badge-warning flex items-center gap-1"
                      animate={{ opacity: [1, 0.5, 1] }}
                      transition={{ duration: 1, repeat: Infinity }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-warning animate-pulse" />
                      Recalculating...
                    </motion.span>
                  )}
                </div>
                <h2 className="text-2xl font-bold text-text-primary leading-tight">{question}</h2>
              </div>
              {onRecalculate && (
                <motion.button
                  onClick={handleRecalculate}
                  disabled={isRecalculating}
                  className="btn-secondary flex items-center gap-2 whitespace-nowrap"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Recalculate
                </motion.button>
              )}
            </div>

            {/* Progress indicator */}
                        <ProgressIndicator progress={progress} showLabel size="md" />
                      </motion.div>

                      {/* Suggestions or Answer Display */}
                      <AnimatePresence mode="wait">
            {!isAnswered && !showFreeText ? (
              <motion.div
                key="suggestions"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                <SuggestionChips suggestions={suggestions} onSelect={handleSuggestionSelect} />
                <motion.button
                  onClick={() => setShowFreeText(true)}
                  className="w-full mt-4 btn-ghost flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1V4a2 2 0 012-2h4z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 19v-1a2 2 0 012-2h14a2 2 0 012 2v1m-9-4a2 2 0 00-2 2v4a2 2 0 002 2h4a2 2 0 002-2v-1" />
                  </svg>
                  Write custom answer
                </motion.button>
              </motion.div>
            ) : null}

            {showFreeText && (
              <motion.div
                key="freetext"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                <FreeTextInput
                  placeholder="Type your answer..."
                  onSubmit={handleFreeTextSubmit}
                  defaultValue={freeTextValue}
                />
              </motion.div>
            )}

            {isAnswered && !showFreeText && (
              <motion.div
                key="answered"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
              >
                <div className="p-4 bg-surface rounded-xl border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="badge-success">{answerState?.type === 'suggestion' ? 'Selected' : 'Custom answer'}</span>
                    <span className="text-sm text-text-muted">Step {currentStep} complete</span>
                  </div>
                  <p className="text-text-primary">{answer || answerState?.value}</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex items-center justify-between pt-4 border-t border-border">
            <div className="flex items-center gap-2">
              {canGoBack && onBack && (
                <BackButton onClick={handleBack} />
              )}
              {onPause && (
                <motion.button
                  onClick={onPause}
                  className="btn-secondary"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <PauseIcon className="w-4 h-4 mr-2" />
                  Pause
                </motion.button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {canGoForward && (
                <motion.button
                  onClick={() => setDirection('forward')}
                  className="btn-primary"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Next
                  <ArrowRightIcon className="w-4 h-4 ml-2" />
                </motion.button>
              )}
            </div>
          </div>

          {/* Keyboard hints */}
          <div className="flex items-center justify-center gap-6 text-xs text-text-muted">
            <kbd className="kbd">Enter</kbd>
            <span>Confirm</span>
            <kbd className="kbd">Esc</kbd>
            <span>Pause/Back</span>
            <kbd className="kbd">←</kbd>
            <span>Back</span>
            <kbd className="kbd">→</kbd>
            <span>Forward</span>
            <kbd className="kbd">Ctrl+Z</kbd>
            <span>Undo</span>
            <kbd className="kbd">Ctrl+Y</kbd>
            <span>Redo</span>
            <kbd className="kbd">Ctrl+Shift+R</kbd>
            <span>Recalc</span>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}

function PauseIcon({ className = '' }: { className?: string }) {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

function ArrowRightIcon({ className = '' }: { className?: string }) {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
    </svg>
  )
}